<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Swiper from 'swiper'
import { Autoplay, EffectFade, Keyboard, Pagination, Parallax } from 'swiper/modules'

const props = defineProps({
    banner: { type: Array, default: () => [] },
    autoplayDelay: {
        type: Number,
        default: 5000,
        validator: value => Number.isFinite(value) && value >= 1000
    },
    transitionSpeed: {
        type: Number,
        default: 800,
        validator: value => Number.isFinite(value) && value >= 300 && value <= 2000
    },
    preloadDistance: {
        type: Number,
        default: 2,
        validator: value => Number.isInteger(value) && value >= 1 && value <= 5
    },
    buttontitle: { type: String, default: '查看详情' }
})

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined'
const IMAGE_RE = /\.(?:avif|bmp|gif|jpe?g|png|svg|webp)(?:[?#].*)?$/i
const VIDEO_TYPES = {
    webm: 'video/webm',
    ogg: 'video/ogg',
    ogv: 'video/ogg',
    mov: 'video/quicktime',
    m4v: 'video/x-m4v'
}

const contentRef = ref(null)
// Banner1 独有的文字样式标记；首页和 Banner2 直接使用原始数据。
const processText = (text) => {
    if (!text) return text

    const pattern = /（([a-zA-Z][\w-]*)-([^（）]*(?:（[^（）]*）)*[^（）]*)）|\(([a-zA-Z][\w-]*)-([^()]*(?:\([^()]*\))*[^()]*)\)/g
    const processRecursively = (str) => str.replace(pattern, (match, chPrefix, chContent, enPrefix, enContent) => {
        const prefix = chPrefix || enPrefix
        const content = chContent || enContent
        if (!prefix || !content) return match
        return `<span class="Heziyun-${prefix}">${processRecursively(content)}</span>`
    })

    return processRecursively(text)
}

const processBannerData = (bannerList) => bannerList?.map(item => ({
    ...item,
    title: item.title ? processText(item.title) : '',
    details: item.details ? processText(item.details) : '',
    small: item.small ? processText(item.small) : ''
})) || []

const processedBanner = computed(() => processBannerData(props.banner))
const hasSingleBanner = computed(() => props.banner.length <= 1)
const mediaSignature = computed(() => props.banner.map((item, index) => (
    `${index}|${item?.id ?? ''}|${item?.img ?? ''}|${item?.poster ?? ''}`
)).join('||'))

let swiper = null
let mounted = false
let visible = isBrowser ? !document.hidden : true
let hoverPaused = false
let rebuilding = 0
let resizeTimer = 0
let preloadTimer = 0
let visibilityHandler = null
let resizeObserver = null

const slideDurations = new Map()
const durationCache = new Map()
const retryTimers = new Set()

const isImage = url => IMAGE_RE.test(String(url || ''))
const getBannerKey = (item, index) => `${item?.id ?? item?.img ?? 'banner'}-${index}`
const getRealIndex = () => swiper?.realIndex ?? swiper?.activeIndex ?? 0
const getActiveSlide = () => swiper?.slides?.[swiper.activeIndex] || null
const getDuration = () => slideDurations.get(getRealIndex()) || props.autoplayDelay

const getVideoType = url => {
    const ext = String(url || '').split(/[?#]/, 1)[0].split('.').pop()?.toLowerCase()
    return VIDEO_TYPES[ext] || 'video/mp4'
}

const setPaginationPaused = paused => {
    contentRef.value?.classList.toggle('is-hover-paused', paused)
}

const clearRetryTimers = () => {
    retryTimers.forEach(clearTimeout)
    retryTimers.clear()
}

const retryPlay = (video, retries = 2) => {
    if (!retries || !mounted || hoverPaused || !visible) return

    const timer = window.setTimeout(() => {
        retryTimers.delete(timer)
        if (!mounted || hoverPaused || !visible || !video?.isConnected) return
        video.play()?.catch(() => retryPlay(video, retries - 1))
    }, 300)

    retryTimers.add(timer)
}

const playVideo = video => {
    if (!video || hoverPaused || !visible) return

    try {
        video.muted = true
        video.preload = 'auto'
        video.play()?.catch(() => retryPlay(video))
    } catch (error) {
        console.error('Video play error:', error)
    }
}

const playActiveMedia = () => {
    const slide = getActiveSlide()
    if (!slide || hoverPaused || !visible) return

    const image = slide.querySelector('img')
    if (image && !image.complete) image.loading = 'eager'
    playVideo(slide.querySelector('video'))
}

const pauseVideos = () => {
    contentRef.value?.querySelectorAll('video').forEach(video => video.pause())
}

const resetInactiveVideos = () => {
    swiper?.slides?.forEach((slide, index) => {
        if (index === swiper.activeIndex) return

        const video = slide.querySelector('video')
        if (!video) return

        video.pause()
        try {
            video.currentTime = 0
        } catch {
            // 兼容尚未载入元数据的视频。
        }
    })
}

const syncAutoplay = () => {
    if (!swiper?.autoplay || swiper.destroyed || hoverPaused || hasSingleBanner.value) return

    swiper.params.autoplay.delay = getDuration()
    swiper.autoplay.stop()
    swiper.autoplay.start()
}

const preloadAround = center => {
    if (!mounted || !contentRef.value) return

    const start = Math.max(0, center - props.preloadDistance)
    const end = Math.min(props.banner.length - 1, center + props.preloadDistance)

    for (let index = start; index <= end; index += 1) {
        contentRef.value
            .querySelectorAll(`.swiper-slide[data-index="${index}"] img, .swiper-slide[data-index="${index}"] video`)
            .forEach(media => {
                if (media.tagName === 'VIDEO') {
                    if (media.readyState === 0) media.load()
                } else if (!media.complete) {
                    media.loading = 'eager'
                }
            })
    }
}

const schedulePreload = index => {
    clearTimeout(preloadTimer)
    preloadTimer = window.setTimeout(() => preloadAround(index), 50)
}

const readVideoDuration = src => {
    if (!isBrowser || !src) return Promise.resolve(props.autoplayDelay)
    if (durationCache.has(src)) return Promise.resolve(durationCache.get(src))

    return new Promise(resolve => {
        const video = document.createElement('video')
        let finished = false

        const done = duration => {
            if (finished) return
            finished = true
            clearTimeout(timeout)
            video.onloadedmetadata = null
            video.onerror = null
            video.removeAttribute('src')
            try { video.load() } catch {}

            const value = Number.isFinite(duration) && duration > 0
                ? Math.max(duration * 1000, 1000)
                : props.autoplayDelay

            if (duration > 0) durationCache.set(src, value)
            resolve(value)
        }

        const timeout = window.setTimeout(() => done(0), 5000)
        video.preload = 'metadata'
        video.muted = true
        video.onloadedmetadata = () => done(video.duration)
        video.onerror = () => done(0)
        video.src = src
    })
}

const readDurations = () => Promise.all(props.banner.map(item => (
    isImage(item?.img) ? props.autoplayDelay : readVideoDuration(item?.img)
)))

const applyDurations = values => {
    slideDurations.clear()
    values.forEach((value, index) => slideDurations.set(index, value))
}

const destroySwiper = () => {
    if (!swiper) return

    try {
        swiper.destroy(true, true)
    } catch (error) {
        console.error('Swiper destroy error:', error)
    } finally {
        swiper = null
    }
}

const getSwiperOptions = () => ({
    direction: 'horizontal',
    effect: 'fade',
    fadeEffect: { crossFade: true },
    autoplay: hasSingleBanner.value ? false : {
        delay: slideDurations.get(0) || props.autoplayDelay,
        disableOnInteraction: false,
        waitForTransition: true,
        pauseOnMouseEnter: false
    },
    parallax: true,
    speed: props.transitionSpeed,
    loop: !hasSingleBanner.value,
    loopAdditionalSlides: 0,
    loopedSlides: Math.min(props.banner.length, 2),
    keyboard: { enabled: true, onlyInViewport: true },
    pagination: {
        el: contentRef.value?.querySelector('.swiper-pagination'),
        type: 'bullets',
        clickable: true,
        renderBullet: (index, className) => {
            const duration = slideDurations.get(index) || props.autoplayDelay
            return `<span class="${className}" style="--bullet-progress-duration:${duration}ms"></span>`
        }
    },
    watchSlidesProgress: true,
    modules: [EffectFade, Pagination, Autoplay, Parallax, Keyboard],
    on: {
        slideChange: () => schedulePreload(getRealIndex()),
        slideChangeTransitionStart: pauseVideos,
        slideChangeTransitionEnd: () => {
            playActiveMedia()
            resetInactiveVideos()
            syncAutoplay()
        },
        touchStart: () => swiper?.autoplay?.stop(),
        touchEnd: () => {
            if (mounted && !hoverPaused) syncAutoplay()
        }
    }
})

const initSwiper = async () => {
    if (!mounted || !props.banner.length || swiper || !contentRef.value) return

    await nextTick()
    if (!mounted || !contentRef.value) return

    try {
        swiper = new Swiper(contentRef.value, getSwiperOptions())
        playActiveMedia()
        syncAutoplay()
        schedulePreload(getRealIndex())
    } catch (error) {
        swiper = null
        console.error('Swiper initialization error:', error)
    }
}

const rebuildSwiper = async () => {
    const version = ++rebuilding
    clearRetryTimers()
    destroySwiper()

    if (!props.banner.length) {
        slideDurations.clear()
        return
    }

    const durations = await readDurations()
    if (!mounted || version !== rebuilding) return

    applyDurations(durations)
    await initSwiper()
}

const refreshDurations = async () => {
    const version = rebuilding
    const durations = await readDurations()
    if (!mounted || version !== rebuilding) return

    applyDurations(durations)
    swiper?.pagination?.render()
    swiper?.pagination?.update()
    syncAutoplay()
}

const handleVideoLoaded = event => {
    const video = event?.target
    if (video && getActiveSlide()?.contains(video)) playVideo(video)
}

const handleMouseEnter = () => {
    hoverPaused = true
    swiper?.autoplay?.stop()
    pauseVideos()
    setPaginationPaused(true)
}

const handleMouseLeave = () => {
    hoverPaused = false
    setPaginationPaused(false)
    playActiveMedia()
    syncAutoplay()
}

const setupVisibility = () => {
    if (!isBrowser) return

    visibilityHandler = () => {
        visible = !document.hidden

        if (!visible) {
            swiper?.autoplay?.stop()
            pauseVideos()
            setPaginationPaused(true)
        } else if (!hoverPaused) {
            setPaginationPaused(false)
            playActiveMedia()
            syncAutoplay()
        }
    }

    document.addEventListener('visibilitychange', visibilityHandler)
}

const setupResizeObserver = () => {
    if (!isBrowser || !contentRef.value || !window.ResizeObserver) return

    resizeObserver = new ResizeObserver(entries => {
        if (!mounted || !entries.length) return
        clearTimeout(resizeTimer)
        resizeTimer = window.setTimeout(() => swiper?.update(), 100)
    })
    resizeObserver.observe(contentRef.value)
}

watch(mediaSignature, () => {
    if (mounted) rebuildSwiper()
})

watch(() => props.autoplayDelay, () => {
    if (mounted) refreshDurations()
})

watch(() => props.transitionSpeed, speed => {
    if (swiper && !swiper.destroyed) swiper.params.speed = speed
})

watch(() => props.preloadDistance, () => {
    if (mounted) schedulePreload(getRealIndex())
})

onMounted(() => {
    mounted = true
    setupVisibility()
    setupResizeObserver()
    rebuildSwiper()
})

onBeforeUnmount(() => {
    mounted = false
    rebuilding += 1
    clearTimeout(resizeTimer)
    clearTimeout(preloadTimer)
    clearRetryTimers()

    if (visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler)
    resizeObserver?.disconnect()
    pauseVideos()
    destroySwiper()
    slideDurations.clear()
    durationCache.clear()
})
</script>

<template>
    <div
        ref="contentRef"
        class="Heziyunswiper swiper swiper-container-h creative-fullpage--slider banner-horizental"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
    >
        <div class="swiper-wrapper">
            <div
                v-for="(item, index) in processedBanner"
                :key="getBannerKey(item, index)"
                class="swiper-slide"
                :data-index="index"
            >
                <a-spin :loading="false" tip="This may take a while...">
                    <div class="slider-inner" data-swiper-parallax="100">
                        <a-image
                            v-if="isImage(item.img)"
                            :src="item.img"
                            show-loader
                            :preview="false"
                        />

                        <video
                            v-else
                            preload="metadata"
                            muted
                            playsinline
                            webkit-playsinline
                            x5-playsinline
                            x5-video-player-type="h5"
                            x5-video-player-fullscreen="true"
                            x5-video-orientation="portrait"
                            disablePictureInPicture
                            :poster="item.poster || ''"
                            @loadedmetadata="handleVideoLoaded"
                        >
                            <source :src="item.img" :type="getVideoType(item.img)" />
                        </video>

                        <div class="swiper-content" data-swiper-parallax="2000">
                            <div v-if="item.small" class="Hui-label" v-html="item.small"></div>
                            <div class="title-area">
                                <span class="title" v-html="item.title"></span>
                            </div>
                            <p class="disc" v-html="item.details"></p>
                            <div class="creative-btn--wrap">
                                <a class="creative-slide--btn Hui-lightSpeed" role="button" :href="item.url">
                                    <div class="creative-btn--label">
                                        <div class="creative-btn__text">{{ $t('index.bannerButton') }}</div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </a-spin>
            </div>
        </div>
        <div v-if="!hasSingleBanner" class="swiper-pagination"></div>
    </div>
</template>

<style lang="less">
@font-face {
    font-family: "italices";
    font-weight: 400;
    src: url("@/static/font/file/italics.woff2") format("woff2");
    font-display: swap;
}

.banner-horizental .arco-image {
    width: 100%;
    height: 100%;
}

.Heziyunswiper {
    position: relative;
    height: 100%;

    @media (max-width: 768px) {
        height: 75vw !important;
    }
}

.creative-fullpage--slider {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    width: 100%;
    background-color: #ffffff;
    clip-path: none !important;

    @media (max-width: 768px) {
        margin-top: 0;
    }

    .slider-inner {
        position: relative;
        height: 100%;
        overflow: hidden;
        isolation: isolate;
        background: #f2f2f2;

        &::before {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: 0;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.72), transparent);
            content: "";
            pointer-events: none;
            transform: translate3d(-100%, 0, 0);
            animation: hzp-motion-shimmer 1.2s linear infinite;
        }

        > * {
            position: relative;
            z-index: 1;
        }

        img,
        video {
            width: 100%;
            height: 100%;
            object-fit: cover;
            background-color: #fff;
        }

        .swiper-content {
            position: absolute;
            top: 45%;
            left: 50%;
            z-index: 1;
            width: 80rem;
            transform: translate(-50%, -50%) !important;

            @media (max-width: 991px) {
                left: 0;
                width: 100%;
                text-align: center;
                transform: translate(0, -50%) !important;
            }

            .Hui-label {
                display: none;
                width: fit-content;
                margin-bottom: 0.5rem;
                margin-left: 0.5rem;
                overflow: hidden;
                color: #fff;

                @media (max-width: 768px) {
                    padding: 0.5vw 4vw;
                    margin: 2vw auto;
                    font-size: 3.5vw;
                }
            }

            .title-area {
                .tag {
                    margin-top: 0;
                    margin-bottom: 0.5rem;
                    color: #ffffff;
                    font-size: 1.3rem;
                    font-weight: 900;

                    @media (max-width: 768px) {
                        display: none;
                    }
                }

                .title {
                    display: none;
                    color: #181818;
                    font-family: "italices";
                    font-size: 2.7rem;
                    font-weight: 500;
                    line-height: 1.1;
                    text-decoration: none;
                    text-transform: uppercase;

                    @media (max-width: 768px) {
                        display: block;
                        padding: 0 5vw;
                        color: #000;
                        font-size: 7vw;
                        font-weight: 900;
                    }
                }
            }

            p.disc {
                display: none;
                width: 30rem;
                margin: 1rem 0;
                color: #181818;
                font-family: "思源黑";
                font-size: 0.8rem;
                font-weight: 400;
                line-height: 1.7;

                @media (max-width: 768px) {
                    display: -webkit-box !important;
                    width: 100%;
                    margin: 3vw 0 0;
                    padding: 0 5vw;
                    overflow: hidden;
                    font-size: 4vw;
                    line-height: 7vw;
                    text-overflow: ellipsis;
                    word-break: break-word;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 2;

                    br {
                        display: none;
                    }
                }

                .highlight {
                    color: #007aff;
                }
            }
        }
    }

    .swiper-slide {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        overflow: hidden;
        text-align: left;
    }
}

.banner-horizental {
    height: 35rem;

    @media (max-width: 768px) {
        height: auto;
        padding-top: 15vw;
    }
}

.swiper-pagination-fraction,
.swiper-pagination-custom,
.swiper-horizontal > .swiper-pagination-bullets,
.swiper-pagination-bullets.swiper-pagination-horizontal {
    position: absolute;
    bottom: 7.5rem !important;
    left: 50% !important;
    display: flex;
    gap: 8px;
    width: 80rem !important;
    transform: translateX(-50%);

    @media (max-width: 768px) {
        bottom: 3vw !important;
        justify-content: center;
        width: auto;
        margin: 0;
    }
}

.swiper-pagination-bullet {
    position: relative;
    width: 40px !important;
    height: 4px !important;
    overflow: hidden;
    cursor: pointer;
    background-color: rgba(38, 38, 38, 0.15) !important;
    border-radius: 0 !important;
    opacity: 1 !important;

    @media (max-width: 768px) {
        width: 9vw;
        height: 0.7vw;
    }
}

.swiper-pagination-bullet-active::before {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    content: "";
    background: #007aff;
    transform: translate3d(-100%, 0, 0);
    animation: hzp-banner-progress var(--bullet-progress-duration, 5000ms) linear forwards;
}

.banner-horizental.is-hover-paused .swiper-pagination-bullet-active::before {
    animation-play-state: paused !important;
}

.swiper-slide-active {
    .slider-inner .swiper-content {
        .Hui-label {
            display: block;
            animation: hzp-banner-content-in var(--hzp-motion-duration) var(--hzp-motion-ease-out) both;
        }

        .title-area .title {
            display: block;
            animation: hzp-banner-content-in var(--hzp-motion-duration) var(--hzp-motion-ease-out) 0.06s both;
        }

        p.disc {
            display: block;
            animation: hzp-banner-content-in var(--hzp-motion-duration) var(--hzp-motion-ease-out) 0.12s both;
        }
    }

    .creative-btn--wrap .creative-slide--btn {
        display: inline-flex;
        animation: hzp-banner-content-in var(--hzp-motion-duration) var(--hzp-motion-ease-out) 0.18s both;
    }
}

.creative-btn--wrap .creative-slide--btn {
    position: relative;
    display: none;
    align-items: center;
    justify-content: center;
    padding: 0.7rem 2.5rem;
    color: #ffffff;
    font-family: "思源黑";
    font-size: 14px;
    font-weight: 400;
    white-space: nowrap;
    text-decoration: none;
    cursor: pointer;
    user-select: none;
    background-color: #0052d9;
    outline: none;
    box-shadow: 0 12px 40px rgba(11, 83, 255, 0.24);
    transition: opacity 0.3s ease;
    backface-visibility: hidden;
    will-change: transform;

    @media (max-width: 768px) {
        margin-top: 3vw;
        padding: 2vw 5vw;
        font-size: 4vw;
    }

    &:hover {
        opacity: 0.8;
    }
}

@keyframes hzp-banner-progress {
    from {
        transform: translate3d(-100%, 0, 0);
    }

    to {
        transform: translate3d(0, 0, 0);
    }
}

@keyframes hzp-banner-content-in {
    from {
        opacity: 0;
        transform: translateX(-20px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .creative-fullpage--slider .slider-inner::before {
        animation: none;
    }

    .swiper-pagination-bullet-active::before,
    .swiper-slide-active .Hui-label,
    .swiper-slide-active .title-area .title,
    .swiper-slide-active p.disc,
    .swiper-slide-active .creative-slide--btn {
        animation-delay: 0s !important;
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
    }
}
</style>
